import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, ComposedChart } from 'recharts';
import api from '../api';
import InvoicePreview from './InvoicePreview';

const MONTH_NAMES = [
  'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs',
  'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'
];

const LINE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

const Dashboard = ({ setPage }) => {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chart state
  const now = new Date();
  const [chartYear, setChartYear] = useState(now.getFullYear());
  const [chartMonth, setChartMonth] = useState(now.getMonth() + 1);
  const [chart, setChart] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/invoices/stats'),
      api.get('/invoices'),
    ]).then(([statsRes, invoicesRes]) => {
      setStats(statsRes.data);

      // Virsraksts „Pēdējie 10 izrakstītie rēķini" → kārtojam striktā hronoloģijā:
      // datums DESC → created_at DESC → id DESC. Iepriekš bija status-priority
      // sortēšana (overdue pirmie), kas izgrūda jaunākos paid/draft no saraksta.
      const toTime = (v) => {
        if (!v) return 0;
        const t = new Date(v).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      const sorted = (invoicesRes.data || []).slice().sort((a, b) => {
        const da = toTime(a.date), dbt = toTime(b.date);
        if (da !== dbt) return dbt - da;
        const ca = toTime(a.created_at), cb = toTime(b.created_at);
        if (ca !== cb) return cb - ca;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });
      setRecentInvoices(sorted.slice(0, 10));
    }).catch(() => {
      setStats({ total: 0, paid: 0, overdue: 0, paidAmount: 0, clientCount: 0 });
    }).finally(() => setLoading(false));
  }, []);

  const loadChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const r = await api.get(`/reports/dashboard-chart?year=${chartYear}&month=${chartMonth}&years=3`);
      setChart(r.data);
    } catch {
      setChart(null);
    } finally {
      setChartLoading(false);
    }
  }, [chartYear, chartMonth]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const fmt = (n) => Number(n || 0).toLocaleString('lv-LV', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div className="loading">Ielādē…</div>;

  // Years for the dropdown — current year and 4 earlier
  const years = Array.from({ length: 5 }).map((_, i) => now.getFullYear() - i);

  return (
    <>
      <div className="page-header">
        <span className="page-title">Panelis</span>
      </div>
      <div className="page-content">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Rēķini kopā</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Apmaksāti</div>
            <div className="stat-value">{stats.paid}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Kavēti</div>
            <div className="stat-value" style={{ color: stats.overdue > 0 ? '#dc2626' : undefined }}>{stats.overdue}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Ieņēmumi</div>
            <div className="stat-value" style={{ fontSize: 20 }}>€{fmt(stats.paidAmount)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Klienti</div>
            <div className="stat-value">{stats.clientCount}</div>
          </div>
        </div>

        {/* ── Ieņēmumu grafiks ── */}
        <div style={{
          background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Ieņēmumu grafiks</h3>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={chartMonth} onChange={e => setChartMonth(Number(e.target.value))}
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={chartYear} onChange={e => setChartYear(Number(e.target.value))}
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {chartLoading ? (
            <div className="loading" style={{ padding: 40 }}>Ielādē grafiku…</div>
          ) : !chart ? (
            <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>Nav datu</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, marginBottom: 8, flexWrap: 'wrap' }}>
                {chart.seriesYears.map((y, i) => (
                  <span key={y} style={{ color: LINE_COLORS[i] }}>
                    ● <strong>{y}</strong>: €{fmt(chart.totals.revenue[y] || 0)}
                  </span>
                ))}
                <span style={{ color: '#ef4444' }}>
                  ● <strong>Izdevumi ({chart.year})</strong>: €{fmt(chart.totals.expenses)}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chart.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                  <Tooltip
                    formatter={(v) => `€${fmt(v)}`}
                    labelFormatter={(l) => `${l}. ${MONTH_NAMES[chart.month - 1]} ${chart.year}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="expenses" name={`Izdevumi (${chart.year})`} fill="#fca5a5" />
                  {chart.seriesYears.map((y, i) => (
                    <Line
                      key={y}
                      type="monotone"
                      dataKey={`revenue_${y}`}
                      name={`Ieņēmumi ${y}`}
                      stroke={LINE_COLORS[i]}
                      strokeWidth={i === 0 ? 2.5 : 1.5}
                      dot={{ r: i === 0 ? 3 : 2 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        <div className="table-container">
          <div className="table-toolbar">
            <h3>Pēdējie 10 izrakstītie rēķini</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage('invoices')}>Skatīt visus</button>
          </div>
          {recentInvoices.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <p>Nav rēķinu. <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={() => setPage('invoices')}>Izveidot pirmo rēķinu</button></p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Rēķins #</th>
                  <th>Klients</th>
                  <th>Datums</th>
                  <th>Kopā</th>
                  <th>Statuss</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map(inv => (
                  <tr key={inv.id} onClick={() => setPreviewId(inv.id)}
                    style={{ cursor: 'pointer' }}
                    title="Noklikšķini, lai apskatītu priekšskatījumu">
                    <td style={{ fontWeight: 500, color: '#2563eb' }}>{inv.invoice_number}</td>
                    <td>{inv.client_name || '—'}</td>
                    <td>{inv.date ? new Date(inv.date).toLocaleDateString('lv-LV') : '—'}</td>
                    <td>€{fmt(inv.total_incl_vat)}</td>
                    <td><span className={`badge badge-${inv.status}`}>{inv.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {previewId && (
        <InvoicePreview invoiceId={previewId} onClose={() => setPreviewId(null)} />
      )}
    </>
  );
};

export default Dashboard;
